import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  GetApp as DownloadIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const buyingSteps = [
  {
    title: 'Get Your Finances Ready',
    description: 'Prepare your financial foundation for homebuying',
    content: [
      'Check and improve your credit score (aim for 620+ for conventional loans)',
      'Save for down payment (3-20% depending on loan type)',
      'Gather financial documents (pay stubs, tax returns, bank statements)',
      'Get pre-approved for a mortgage (not just pre-qualified)',
      'Research first-time buyer programs in New Jersey',
    ],
    tips: 'New Jersey offers down payment assistance programs for first-time buyers. Contact NJ Housing and Mortgage Finance Agency for details.',
    timeframe: '2-6 months before house hunting',
  },
  {
    title: 'Choose Your Location',
    description: 'Research Middlesex County communities',
    content: [
      'Research school districts (Middlesex County has excellent options)',
      'Consider commute times to work (proximity to NYC, trains, highways)',
      'Explore neighborhoods (Woodbridge, Edison, Perth Amboy, New Brunswick)',
      'Check property taxes (vary significantly by township)',
      'Visit areas at different times of day and week',
    ],
    tips: 'Woodbridge offers 10 distinct communities, each with unique character. Edison has excellent schools and diversity. Consider your lifestyle priorities.',
    timeframe: '1-3 months before house hunting',
  },
  {
    title: 'Find a Real Estate Agent',
    description: 'Choose an experienced local agent',
    content: [
      'Interview at least 3 agents familiar with Middlesex County',
      'Ask about their experience with first-time buyers',
      'Verify they understand local market conditions',
      'Discuss their communication style and availability',
      'Get references from recent clients',
    ],
    tips: 'Look for agents who specialize in your target townships and price range. Local knowledge is crucial in Middlesex County.',
    timeframe: '2-4 weeks before house hunting',
  },
  {
    title: 'Start House Hunting',
    description: 'Begin your search strategically',
    content: [
      'Create a realistic wish list (needs vs. wants)',
      'Set a firm budget (include closing costs and moving expenses)',
      'View homes in person when possible',
      'Take notes and photos during viewings',
      'Research comparable sales in the area',
    ],
    tips: 'The Middlesex County market moves quickly. Be prepared to make decisions faster than you might expect.',
    timeframe: '2-8 weeks (varies by market)',
  },
  {
    title: 'Make an Offer',
    description: 'Submit a competitive offer',
    content: [
      'Work with your agent to determine fair market value',
      'Include appropriate contingencies (inspection, financing, appraisal)',
      'Submit earnest money deposit (typically 1-3% of purchase price)',
      'Set realistic timelines for contingencies',
      'Be prepared for counteroffers',
    ],
    tips: 'In competitive markets, consider waiving minor contingencies, but never skip the inspection contingency.',
    timeframe: '1-7 days after finding the right home',
  },
  {
    title: 'Get Home Inspection',
    description: 'Thoroughly evaluate the property',
    content: [
      'Hire a licensed home inspector within your contingency period',
      'Attend the inspection to ask questions',
      'Review the inspection report carefully',
      'Negotiate repairs or credits if issues are found',
      'Consider specialized inspections (radon, septic, well)',
    ],
    tips: 'New Jersey requires 10 days for inspection unless otherwise specified. Don\'t waive this contingency!',
    timeframe: '5-10 days after offer acceptance',
  },
  {
    title: 'Finalize Your Mortgage',
    description: 'Complete the lending process',
    content: [
      'Submit any additional documents requested by lender',
      'Schedule and complete the appraisal',
      'Review loan estimate and closing disclosure carefully',
      'Lock in your interest rate when appropriate',
      'Obtain homeowners insurance and provide proof to lender',
    ],
    tips: 'Stay in close contact with your lender. Respond to requests quickly to avoid delays.',
    timeframe: '30-45 days after offer acceptance',
  },
  {
    title: 'Prepare for Closing',
    description: 'Get ready for the final step',
    content: [
      'Schedule final walk-through 24-48 hours before closing',
      'Review closing disclosure at least 3 days before closing',
      'Arrange for certified funds for down payment and closing costs',
      'Confirm closing date, time, and location',
      'Prepare for utility transfers and address changes',
    ],
    tips: 'Bring a valid photo ID and be prepared to sign many documents. Don\'t hesitate to ask questions about anything you don\'t understand.',
    timeframe: '1-2 weeks before closing',
  },
];

const localInsights = [
  {
    township: 'Woodbridge',
    highlights: [
      'Great value with diverse communities (Avenel, Colonia, Fords, etc.)',
      'Excellent train access to NYC via NJ Transit',
      'Strong school system with multiple elementary schools',
      'Established neighborhoods with mature trees',
    ],
    considerations: [
      'Property taxes vary by neighborhood',
      'Some areas near industrial zones',
      'Traffic on Route 1 and Garden State Parkway',
    ],
    avgHomePrice: '$450,000 - $650,000',
  },
  {
    township: 'Edison',
    highlights: [
      'Top-rated school district in New Jersey',
      'Diverse, family-friendly communities',
      'Close to major highways and NYC transportation',
      'Strong resale values',
    ],
    considerations: [
      'Higher property taxes than some surrounding areas',
      'Competitive market with multiple offers common',
      'Limited inventory in popular price ranges',
    ],
    avgHomePrice: '$500,000 - $750,000',
  },
  {
    township: 'Perth Amboy',
    highlights: [
      'Waterfront location with beautiful views',
      'More affordable than neighboring townships',
      'Historic downtown with character',
      'Close to Staten Island and Manhattan',
    ],
    considerations: [
      'Urban environment may not suit all buyers',
      'Some areas undergoing revitalization',
      'Flood zones in waterfront areas',
    ],
    avgHomePrice: '$300,000 - $500,000',
  },
  {
    township: 'New Brunswick',
    highlights: [
      'College town energy with Rutgers University',
      'Cultural attractions and dining options',
      'Good public transportation',
      'Diverse housing options',
    ],
    considerations: [
      'Student population affects some neighborhoods',
      'Parking can be challenging in downtown areas',
      'Property taxes higher than county average',
    ],
    avgHomePrice: '$350,000 - $550,000',
  },
];

const firstTimeBuyerPrograms = [
  {
    program: 'NJ First-Time Homebuyer Program',
    description: 'Down payment assistance and favorable loan terms',
    benefits: ['Down payment assistance up to $10,000', 'Reduced interest rates', 'No private mortgage insurance in some cases'],
    eligibility: 'First-time buyers, income limits apply',
    contact: 'NJ Housing and Mortgage Finance Agency',
  },
  {
    program: 'FHA Loans',
    description: 'Federal program for first-time buyers',
    benefits: ['Down payment as low as 3.5%', 'Flexible credit requirements', 'Assumable loans'],
    eligibility: 'Primary residence, debt-to-income limits',
    contact: 'Any FHA-approved lender',
  },
  {
    program: 'VA Loans (Veterans)',
    description: 'No down payment loans for eligible veterans',
    benefits: ['$0 down payment', 'No private mortgage insurance', 'Competitive interest rates'],
    eligibility: 'Eligible veterans and service members',
    contact: 'VA-approved lenders',
  },
  {
    program: 'USDA Rural Development',
    description: 'Zero down payment for eligible rural areas',
    benefits: ['$0 down payment', 'Below-market interest rates', 'No private mortgage insurance'],
    eligibility: 'Income limits, property must be in eligible area',
    contact: 'USDA-approved lenders',
  },
];

const FirstTimeBuyerMiddlesex: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [expandedInsight, setExpandedInsight] = useState<string | false>(false);

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const handleInsightChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedInsight(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>First-Time Homebuyer Guide for Middlesex County | MAD Title Agency</title>
        <meta
          name="description"
          content="Complete guide for first-time homebuyers in Middlesex County, NJ. Step-by-step process, local insights for Woodbridge, Edison, and surrounding areas."
        />
        <meta
          name="keywords"
          content="first time homebuyer Middlesex County, Woodbridge home buying guide, Edison real estate, New Jersey first time buyer programs, Middlesex County real estate"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            First-Time Homebuyer Guide
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="First-Time Buyer Guide"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                First-Time Homebuyer Guide for Middlesex County
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Your complete roadmap to buying your first home in Woodbridge, Edison, and surrounding areas
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Complete Guide
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <HomeIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Your First Home
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Middlesex County Guide
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Welcome to Homeownership in Middlesex County
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Buying your first home is one of life's biggest milestones, and Middlesex County offers 
                some of New Jersey's best communities for new homeowners. From the diverse neighborhoods 
                of Woodbridge to the excellent schools of Edison, this area provides ideal opportunities 
                for first-time buyers.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                This comprehensive guide walks you through every step of the home buying process, 
                with specific insights about Middlesex County markets, local programs, and practical 
                tips from our experienced team.
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Good News!</strong> Middlesex County offers excellent value, with median home 
                  prices below those in Bergen and Hunterdon counties, while still providing easy 
                  access to NYC and excellent amenities.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
                <LocationIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Why Middlesex County?
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Excellent school districts" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Easy NYC commute" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Diverse communities" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Strong property values" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Step-by-Step Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Your 8-Step Path to Homeownership
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Follow this proven process to navigate your first home purchase successfully:
          </Typography>

          <Stepper activeStep={activeStep} orientation="vertical">
            {buyingSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ py: 2 }}>
                    <Chip
                      label={step.timeframe}
                      size="small"
                      sx={{ mb: 2, backgroundColor: '#f59e0b', color: 'white' }}
                    />
                    <List>
                      {step.content.map((item, itemIndex) => (
                        <ListItem key={itemIndex}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a' }} />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Local Tip:</strong> {step.tips}
                      </Typography>
                    </Alert>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Local Township Insights */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Middlesex County Township Guide
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Each township in Middlesex County offers unique advantages. Here's what first-time buyers should know:
          </Typography>

          {localInsights.map((township, index) => (
            <Accordion
              key={township.township}
              expanded={expandedInsight === township.township}
              onChange={handleInsightChange(township.township)}
              sx={{ mb: 2, border: '1px solid #e2e8f0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {township.township}
                    </Typography>
                  </Box>
                  <Chip
                    label={township.avgHomePrice}
                    size="small"
                    sx={{
                      backgroundColor: '#1e3a8a',
                      color: 'white',
                      mr: 2,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#16a34a' }}>
                      Highlights
                    </Typography>
                    <List>
                      {township.highlights.map((highlight, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a' }} />
                          </ListItemIcon>
                          <ListItemText primary={highlight} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#f59e0b' }}>
                      Considerations
                    </Typography>
                    <List>
                      {township.considerations.map((consideration, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <WarningIcon sx={{ color: '#f59e0b' }} />
                          </ListItemIcon>
                          <ListItemText primary={consideration} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* First-Time Buyer Programs */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            First-Time Buyer Programs & Assistance
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Take advantage of these programs designed to help first-time buyers:
          </Typography>

          <Grid container spacing={4}>
            {firstTimeBuyerPrograms.map((program, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MoneyIcon sx={{ color: '#16a34a', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {program.program}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {program.description}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Benefits:
                    </Typography>
                    <List dense>
                      {program.benefits.map((benefit, idx) => (
                        <ListItem key={idx} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ color: '#16a34a', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary={benefit} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      <strong>Eligibility:</strong> {program.eligibility}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Contact:</strong> {program.contact}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Common Mistakes to Avoid */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Common First-Time Buyer Mistakes to Avoid
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fef3c7' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Financial Mistakes
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Maxing out your budget"
                      secondary="Leave room for unexpected expenses"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Forgetting closing costs"
                      secondary="Budget 2-5% of purchase price"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Making major purchases before closing"
                      secondary="Avoid new debt until after closing"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fee2e2' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Process Mistakes
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Skipping the home inspection"
                      secondary="Never waive this important protection"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Not researching the neighborhood"
                      secondary="Visit at different times and days"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Falling in love with the first house"
                      secondary="View multiple properties for comparison"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Next Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Ready to Get Started?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <MoneyIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Calculate Costs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Estimate your closing costs for Middlesex County properties.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/closing-cost-calculator"
                  fullWidth
                >
                  Use Calculator
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Closing Checklist
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Interactive checklist for your Middlesex County closing.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/middlesex-county-closing-checklist"
                  fullWidth
                >
                  View Checklist
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <SchoolIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Learn More
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Explore our complete real estate glossary.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/nj-real-estate-glossary"
                  fullWidth
                >
                  View Glossary
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Ready to Buy Your First Home?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced team helps first-time buyers navigate every step of the process in Middlesex County.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Start Your Journey
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="tel:+17328745425"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Call Our Experts
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default FirstTimeBuyerMiddlesex; 